import type { AppRole } from "@shop-ledger/shared-types";
import type { NavItem, NavSection } from "@shop-ledger/ui-components";

/**
 * system_architecture.md §10's role table, as it applies to *this* app specifically:
 *
 * | Role           | dashboard-admin access                                                     |
 * |----------------|------------------------------------------------------------------------------|
 * | TELLER         | None at all — "pos-pwa only" per §10. This app redirects a TELLER straight to `/not-authorized` with a message pointing at the till. |
 * | BRANCH_MANAGER | A scoped slice: their branch's stock levels + the sync_conflicts "Needs Review" queue. Explicitly NOT chart of accounts/ledger/branch creation/cross-branch reports (§10). |
 * | ACCOUNTANT     | The full Accountant Control Panel: General Ledger, Chart of Accounts, journal entries, P&L, invoice/bill aging, payments, conflicts. NOT POS operational actions or user/branch management. |
 * | ECOM_MGR       | Inventory (catalog) + the online order queue (`Sale.source = ECOMMERCE`). NOT ledger/accounting views or other branches' in-store sales detail. |
 * | OWNER          | Everything above, unioned (§10: "Owner is a superset, not a separate design"). |
 *
 * As with `pos-pwa/src/roles/permissions.ts`, **this is a UX convenience only** — every
 * role boundary below is mirrored (and actually enforced) by the backend's own
 * `require_roles(...)` dependencies (`backend/BACKEND_ARCHITECTURE.md`'s API surface
 * table) and RLS. A role seeing a nav item here is not a security guarantee; a role NOT
 * seeing one here does not mean the backend would reject it if called directly — the two
 * are kept in sync by hand, not derived from a single shared source, so a reviewer should
 * cross-check both sides when changing either.
 *
 * **One real backend/spec mismatch found while wiring this up**: §10 describes ECOM_MGR as
 * owning "product catalog + gallery management," but `POST/PATCH /inventory` are gated
 * `require_roles(OWNER, BRANCH_MANAGER)` only (`backend/app/api/v1/inventory.py`) — an
 * ECOM_MGR calling either would get a 403 regardless of what this app renders. This app
 * honors the backend's actual behavior: ECOM_MGR gets read access to the inventory list,
 * but the create/edit controls are hidden for that role (see `InventoryListPage.tsx`),
 * with a note rather than silently letting them hit a 403.
 */

export const ROLES_WITH_APP_ACCESS: readonly AppRole[] = ["OWNER", "BRANCH_MANAGER", "ACCOUNTANT", "ECOM_MGR"];

export const ROLES_WITH_LEDGER_ACCESS: readonly AppRole[] = ["OWNER", "ACCOUNTANT"];
export const ROLES_WITH_INVOICE_ACCESS: readonly AppRole[] = ["OWNER", "ACCOUNTANT"];
export const ROLES_WITH_PAYMENTS_ACCESS: readonly AppRole[] = ["OWNER", "ACCOUNTANT"];
export const ROLES_WITH_ASSETS_ACCESS: readonly AppRole[] = ["OWNER", "ACCOUNTANT"];
/** Mirrors `require_roles(OWNER, ACCOUNTANT, BRANCH_MANAGER)` on
 * `POST/GET /expenses` (`backend/app/api/v1/expenses.py`) — the simplified-dashboard
 * "Add Expense" feature (business-owner walkthrough follow-up). ECOM_MGR excluded, same
 * reasoning as the backend route: no reason to see or log cash expenses. */
export const ROLES_WITH_EXPENSES_ACCESS: readonly AppRole[] = ["OWNER", "ACCOUNTANT", "BRANCH_MANAGER"];

/** Read access to the inventory list. Write (create/edit) is a stricter subset — see
 * `ROLES_WITH_INVENTORY_WRITE` — matching the backend's own split. */
export const ROLES_WITH_INVENTORY_ACCESS: readonly AppRole[] = ["OWNER", "BRANCH_MANAGER", "ACCOUNTANT", "ECOM_MGR"];
/**
 * Mirrors `require_roles(AppRole.OWNER, AppRole.BRANCH_MANAGER, AppRole.ECOM_MGR)` on
 * `POST/PATCH /inventory` (`backend/app/api/v1/inventory.py`). **Widened 2026-07-13**
 * (business-owner walkthrough item 9's "one-line decision", §10.2): the backend used to
 * be `OWNER`/`BRANCH_MANAGER` only, which is what this constant previously matched
 * (documented then as a real spec-vs-code mismatch against §10's ECOM_MGR framing) — the
 * backend itself was widened to close that gap, so this now matches it exactly instead of
 * hiding write controls from a role the backend actually accepts writes from.
 */
export const ROLES_WITH_INVENTORY_WRITE: readonly AppRole[] = ["OWNER", "BRANCH_MANAGER", "ECOM_MGR"];

export const ROLES_WITH_CONFLICTS_ACCESS: readonly AppRole[] = ["OWNER", "ACCOUNTANT", "BRANCH_MANAGER"];
export const ROLES_WITH_ONLINE_ORDERS_ACCESS: readonly AppRole[] = ["OWNER", "ECOM_MGR"];

/** `GET/POST /branches` (§10.1) — page-level access is OWNER-only per this task's brief,
 * even though `GET /branches` itself is open to any authenticated role (every branch
 * picker elsewhere in this app calls it directly via `BranchSelect`, not through this
 * page's guard). */
export const ROLES_WITH_BRANCH_ACCESS: readonly AppRole[] = ["OWNER"];

/** `GET /users` (§10.1) — `OWNER` sees every user, `BRANCH_MANAGER` sees only their own
 * branch (backend-enforced). Invite/reassign/suspend controls are OWNER-only within the
 * page itself (`StaffPage.tsx`'s `canManage`), mirroring `POST/PATCH /users`. */
export const ROLES_WITH_STAFF_ACCESS: readonly AppRole[] = ["OWNER", "BRANCH_MANAGER"];

/** `GET/POST /suppliers` (§10.2) — list is open to any authenticated role (every
 * supplier dropdown needs it); nav visibility here matches who'd plausibly create one. */
export const ROLES_WITH_SUPPLIERS_ACCESS: readonly AppRole[] = ["OWNER", "BRANCH_MANAGER", "ACCOUNTANT", "ECOM_MGR"];
/** Mirrors `require_roles(OWNER, BRANCH_MANAGER, ECOM_MGR)` on `POST /suppliers`. */
export const ROLES_WITH_SUPPLIER_WRITE: readonly AppRole[] = ["OWNER", "BRANCH_MANAGER", "ECOM_MGR"];

/** `GET/POST /customers` (§10.3) — no role restriction on the backend at all
 * (`get_current_user` only), so every role with app access can use this page. */
export const ROLES_WITH_CUSTOMERS_ACCESS: readonly AppRole[] = [...ROLES_WITH_APP_ACCESS];

/** `GET/POST /purchase-orders`, `/receive` (§10.4) — list/detail reads are open to any
 * authenticated role; nav visibility here matches who'd plausibly create/receive one plus
 * ACCOUNTANT for financial visibility. */
export const ROLES_WITH_PURCHASE_ORDERS_ACCESS: readonly AppRole[] = ["OWNER", "BRANCH_MANAGER", "ECOM_MGR", "ACCOUNTANT"];
/** Mirrors `require_roles(OWNER, BRANCH_MANAGER, ECOM_MGR)` on create/receive. */
export const ROLES_WITH_PURCHASE_ORDERS_WRITE: readonly AppRole[] = ["OWNER", "BRANCH_MANAGER", "ECOM_MGR"];

/** `POST/GET /stock-transfers` (§10.4) — mirrors `require_roles(OWNER, BRANCH_MANAGER)`
 * on create exactly; nav-gated to the same pair since a read-only view of transfers has
 * little standalone value for other roles in this app. */
export const ROLES_WITH_STOCK_TRANSFER_ACCESS: readonly AppRole[] = ["OWNER", "BRANCH_MANAGER"];

/** `GET /reports/expiring-soon`, `/low-stock` (§10.5) — no role restriction on the
 * backend; nav-gated to the same roles as inventory itself, the closest domain match. */
export const ROLES_WITH_INVENTORY_REPORTS_ACCESS: readonly AppRole[] = [...ROLES_WITH_INVENTORY_ACCESS];

/** Mirrors `require_roles(OWNER, BRANCH_MANAGER, ACCOUNTANT)` on `GET /reports/sales-summary`. */
export const ROLES_WITH_SALES_DASHBOARD_ACCESS: readonly AppRole[] = ["OWNER", "BRANCH_MANAGER", "ACCOUNTANT"];

/**
 * Cross-app SSO link (Part 4 of the demo-tenant/cross-app-SSO plan) — "Open Online Store"
 * in `CrossAppLinks.tsx`, rendered via `NavHeader`. Not a real route (no backend
 * `require_roles` to mirror, since this is an external redirect to a genuinely separate
 * app/origin) — this is purely "who would plausibly want the shortcut," same judgment call
 * this file already makes for read-only nav items like Customers/Suppliers.
 *
 * The equivalent "Open POS" link/`ROLES_WITH_POS_LINK_ACCESS` is gone as of the `pos-pwa`
 * merge — see `ROLES_WITH_POS_LINK_ACCESS` near `BASIC_NAV_ITEMS` below, which now gates a
 * plain in-app link instead of a cross-app SSO redirect.
 */
export const ROLES_WITH_ECOMMERCE_LINK_ACCESS: readonly AppRole[] = ["OWNER", "ECOM_MGR"];

export function canAccessApp(role: AppRole): boolean {
  return ROLES_WITH_APP_ACCESS.includes(role);
}

export function canWriteInventory(role: AppRole): boolean {
  return ROLES_WITH_INVENTORY_WRITE.includes(role);
}

export function canManageBranches(role: AppRole): boolean {
  return ROLES_WITH_BRANCH_ACCESS.includes(role);
}

export function canManageStaff(role: AppRole): boolean {
  return role === "OWNER";
}

/** Mirrors the OWNER-only check `backend/app/api/v1/inventory.py`'s `create_item`/
 * `update_item` enforce on `is_serialized=True` (business-owner request: serial-number
 * tracking adds a real per-sale cost — required serial entry at checkout — so only OWNER
 * should be able to opt an item into it, not every role that can otherwise write
 * inventory). Turning it *off* has no such restriction. */
export function canEnableSerializedTracking(role: AppRole): boolean {
  return role === "OWNER";
}

export function canWriteSuppliers(role: AppRole): boolean {
  return ROLES_WITH_SUPPLIER_WRITE.includes(role);
}

export function canWritePurchaseOrders(role: AppRole): boolean {
  return ROLES_WITH_PURCHASE_ORDERS_WRITE.includes(role);
}

/**
 * 2026-07-13 five-branch integration pass (`backend/BACKEND_ARCHITECTURE.md` §11-§15) —
 * every constant below mirrors a `require_roles(...)` dependency verified directly against
 * `backend/app/api/v1/*.py`, same discipline as everything above. Two exceptions, called
 * out inline: Approval Requests/Rules are gated by `require_permission(...)`, not
 * `require_roles`, so there is no fixed role list to mirror for the ROUTE GUARD (the page
 * itself handles a 403 gracefully instead).
 */

/** `GET/POST/PATCH/DELETE /tax-rates`, `GET/POST/DELETE /exchange-rates` — reads are open
 * to any authenticated user; writes mirror `_CONFIG_ROLES = (OWNER, ACCOUNTANT)`. */
export const ROLES_WITH_TAX_FX_ACCESS: readonly AppRole[] = [...ROLES_WITH_APP_ACCESS];
export const ROLES_WITH_TAX_FX_WRITE: readonly AppRole[] = ["OWNER", "ACCOUNTANT"];

/** `POST/GET /bank-reconciliation/*` — every route is `_BANK_RECON_ROLES = (OWNER,
 * ACCOUNTANT)`, including reads (unlike tax rates, which open reads to everyone). */
export const ROLES_WITH_BANK_RECON_ACCESS: readonly AppRole[] = ["OWNER", "ACCOUNTANT"];

/** `GET /returns` — any authenticated user. `POST /returns` — mirrors `require_roles(OWNER,
 * BRANCH_MANAGER)` (backend comment: "a TELLER accidentally being able to refund arbitrary
 * amounts is a real risk"). */
export const ROLES_WITH_RETURNS_ACCESS: readonly AppRole[] = [...ROLES_WITH_APP_ACCESS];
export const ROLES_WITH_RETURNS_WRITE: readonly AppRole[] = ["OWNER", "BRANCH_MANAGER"];
export function canWriteReturns(role: AppRole): boolean {
  return ROLES_WITH_RETURNS_WRITE.includes(role);
}

/** `GET /promo-codes`, `GET /gift-cards/{code}` — any authenticated user. `POST
 * /promo-codes`, `POST /gift-cards` — mirrors `require_roles(OWNER, BRANCH_MANAGER)`. */
export const ROLES_WITH_PROMOTIONS_ACCESS: readonly AppRole[] = [...ROLES_WITH_APP_ACCESS];
export const ROLES_WITH_PROMOTIONS_WRITE: readonly AppRole[] = ["OWNER", "BRANCH_MANAGER"];
export function canWritePromotions(role: AppRole): boolean {
  return ROLES_WITH_PROMOTIONS_WRITE.includes(role);
}

/** `GET /tenant-settings/loyalty` — any authenticated user; `PATCH` — OWNER only (enforced
 * again inside `LoyaltySettingsPage` itself since it's a single-page read+write surface). */
export const ROLES_WITH_LOYALTY_ACCESS: readonly AppRole[] = [...ROLES_WITH_APP_ACCESS];

/** `GET /shipments` — any authenticated user. `POST/PATCH /shipments` — mirrors
 * `require_roles(OWNER, BRANCH_MANAGER, ECOM_MGR)` (`_WRITE_ROLES` in `shipments.py`). */
export const ROLES_WITH_SHIPMENTS_ACCESS: readonly AppRole[] = [...ROLES_WITH_APP_ACCESS];
export const ROLES_WITH_SHIPMENTS_WRITE: readonly AppRole[] = ["OWNER", "BRANCH_MANAGER", "ECOM_MGR"];
export function canWriteShipments(role: AppRole): boolean {
  return ROLES_WITH_SHIPMENTS_WRITE.includes(role);
}

/**
 * `GET /approval-requests`, `POST .../{id}/{approve,reject}`, `GET/POST /approval-rules`
 * are gated by `require_permission("purchase_order.approve" | "approval_rule.manage")`,
 * NOT `require_roles` — the platform-default seed (migration 0004) grants both to OWNER
 * only, but a tenant OWNER can grant either to another role via a per-user override
 * (`PermissionsPage`). Since there is no "my effective permissions" self-check endpoint,
 * this app can't know client-side whether a non-OWNER has been granted an override, so —
 * unlike every other surface in this file — the nav/route guard here is deliberately
 * broad (every app-access role) rather than hardcoded to OWNER; each page itself catches a
 * 403 from the backend and shows an explanatory banner instead of a raw error. A silent
 * new PO status with no way to even LOOK at the queue would be worse than a route that's
 * occasionally a 403 for someone without the permission.
 */
export const ROLES_WITH_APPROVALS_ACCESS: readonly AppRole[] = [...ROLES_WITH_APP_ACCESS];

/** `GET/PATCH /inventory/import`, `GET /inventory/export` — export is open to any
 * authenticated user; import mirrors inventory's own write roles. Same nav-access list as
 * Inventory itself since it's reached from that page. */
export const ROLES_WITH_BULK_ACCESS: readonly AppRole[] = [...ROLES_WITH_INVENTORY_ACCESS];

/** `GET/PATCH /users/{id}/permissions`, `GET /roles/{role}/permissions` — both
 * `require_roles(OWNER)` exactly (unlike Approval*, these ARE hardcoded role-gated on the
 * backend, so a tight nav guard is safe and correct here). */
export const ROLES_WITH_PERMISSIONS_ACCESS: readonly AppRole[] = ["OWNER"];

/** `GET /notifications/log` — `require_roles(OWNER)`. */
export const ROLES_WITH_NOTIFICATIONS_ACCESS: readonly AppRole[] = ["OWNER"];

/** `/webhooks/*` — every route `require_roles(OWNER)`. */
export const ROLES_WITH_WEBHOOKS_ACCESS: readonly AppRole[] = ["OWNER"];

/** `GET /branding` (§11.1) — open to any authenticated role (applies UI-wide); the page
 * itself gates every write (`PATCH /branding`, logo/favicon upload, `/domains/*`) to OWNER,
 * mirroring `require_roles(OWNER)` on all of those. Nav visibility here matches the write
 * gate since reading branding doesn't need its own settings page for non-owners. */
export const ROLES_WITH_BRANDING_ACCESS: readonly AppRole[] = ["OWNER"];

/**
 * Simplified-dashboard redesign (business-owner walkthrough follow-up): the old flat
 * 34-item `NAV_ITEMS` list is replaced by `BASIC_NAV_ITEMS` (the ~7 things a non-technical
 * business owner needs day to day, shown always) + `NAV_SECTIONS` (everything else,
 * grouped into collapsible sections, shown only when "Advanced" is toggled on — see
 * `lib/useNavMode.ts`). Every underlying `ROLES_WITH_*` constant above is unchanged; only
 * the nav's *shape* changed, not who can see what — `RequireRole` route guards in
 * `App.tsx` are the real enforcement and are untouched by this restructuring.
 *
 * Several old single-purpose pages are now tabs inside one merged page (e.g. Low
 * Stock/Expiring Soon/Bulk Import all live inside `/stock`) — see `App.tsx`'s route table
 * and the `<Navigate>` redirects it keeps for every old URL.
 */
export const ROLES_WITH_POS_LINK_ACCESS: readonly AppRole[] = ["OWNER", "BRANCH_MANAGER"];

/** The one-page quotation/invoice/receipt generator (`/documents`) — a client-side
 * document builder with no backend writes at all (no `require_roles` to mirror), so any
 * role with app access may use it; a TELLER still can't reach it (no app access). */
export const ROLES_WITH_DOCUMENTS_ACCESS: readonly AppRole[] = [...ROLES_WITH_APP_ACCESS];

export const BASIC_NAV_ITEMS: NavItem[] = [
  { key: "dashboard", label: "Dashboard", href: "/dashboard", roles: [...ROLES_WITH_APP_ACCESS] },
  // Merge of pos-pwa into this app (see PosNavHeader.tsx / App.tsx's /pos/* routes) —
  // this is now a plain in-app link, not a cross-app SSO redirect.
  { key: "pos", label: "Point of Sale", href: "/pos/till", roles: [...ROLES_WITH_POS_LINK_ACCESS] },
  { key: "pnl", label: "Profit & Loss", href: "/reports/pnl", roles: [...ROLES_WITH_LEDGER_ACCESS] },
  { key: "stock", label: "Stock", href: "/stock", roles: [...ROLES_WITH_INVENTORY_ACCESS] },
  { key: "sales-dashboard", label: "Sales", href: "/reports/sales", roles: [...ROLES_WITH_SALES_DASHBOARD_ACCESS] },
  { key: "expenses", label: "Expenses", href: "/expenses", roles: [...ROLES_WITH_EXPENSES_ACCESS] },
  // Business-owner request: generating a quotation/invoice/receipt for a client is
  // day-to-day "simple view" work, not an advanced back-office concern.
  { key: "documents", label: "Documents", href: "/documents", roles: [...ROLES_WITH_DOCUMENTS_ACCESS] },
  { key: "staff", label: "Staff", href: "/staff", roles: [...ROLES_WITH_STAFF_ACCESS] },
  // Added per direct business-owner request: "who is ordering online" is basic,
  // day-to-day information, not an advanced/back-office concern.
  { key: "orders", label: "Online Orders", href: "/orders", roles: [...ROLES_WITH_ONLINE_ORDERS_ACCESS] },
];

export const NAV_SECTIONS: NavSection[] = [
  {
    key: "accounting",
    label: "Accounting",
    items: [
      { key: "ledger", label: "General Ledger", href: "/ledger", roles: [...ROLES_WITH_LEDGER_ACCESS] },
      { key: "accounts", label: "Chart of Accounts", href: "/accounts", roles: [...ROLES_WITH_LEDGER_ACCESS] },
      { key: "journal-entry", label: "New Journal Entry", href: "/ledger/new", roles: [...ROLES_WITH_LEDGER_ACCESS] },
      { key: "invoices", label: "Invoices & Bills", href: "/invoices", roles: [...ROLES_WITH_INVOICE_ACCESS] },
      { key: "tax-currency", label: "Tax & Currency", href: "/tax-currency", roles: [...ROLES_WITH_TAX_FX_ACCESS] },
      { key: "payments", label: "Payments", href: "/payments", roles: [...ROLES_WITH_PAYMENTS_ACCESS] },
      { key: "bank-reconciliation", label: "Bank Reconciliation", href: "/bank-reconciliation", roles: [...ROLES_WITH_BANK_RECON_ACCESS] },
    ],
  },
  {
    key: "inventory-supply",
    label: "Inventory & Supply",
    items: [
      { key: "purchase-orders", label: "Purchase Orders", href: "/purchase-orders", roles: [...ROLES_WITH_PURCHASE_ORDERS_ACCESS] },
      { key: "stock-transfers", label: "Stock Transfers", href: "/stock-transfers", roles: [...ROLES_WITH_STOCK_TRANSFER_ACCESS] },
      { key: "suppliers", label: "Suppliers", href: "/suppliers", roles: [...ROLES_WITH_SUPPLIERS_ACCESS] },
    ],
  },
  {
    key: "sales-customers",
    label: "Sales & Customers",
    items: [
      { key: "customers", label: "Customers", href: "/customers", roles: [...ROLES_WITH_CUSTOMERS_ACCESS] },
      { key: "fulfillment", label: "Fulfillment", href: "/fulfillment", roles: [...ROLES_WITH_APP_ACCESS] },
      { key: "promotions", label: "Promotions", href: "/promotions", roles: [...ROLES_WITH_APP_ACCESS] },
    ],
  },
  {
    key: "team-access",
    label: "Team & Access",
    items: [{ key: "team", label: "Team", href: "/team", roles: [...ROLES_WITH_STAFF_ACCESS] }],
  },
  {
    key: "operations",
    label: "Operations",
    items: [
      { key: "approvals", label: "Approvals", href: "/approvals", roles: [...ROLES_WITH_APPROVALS_ACCESS] },
      { key: "conflicts", label: "Needs Review", href: "/conflicts", roles: [...ROLES_WITH_CONFLICTS_ACCESS] },
      // Moved out of the POS kiosk chrome (business-owner request: "hide needs review and
      // quick return from the pos, move those features to dashboard admin") — the /pos/*
      // routes and their RequireRole guards are unchanged; only where they're reachable
      // from moved. Roles mirror the POS_* constants below, which mirror the backend.
      { key: "pos-quick-return", label: "Quick Return (POS)", href: "/pos/returns", roles: ["OWNER", "BRANCH_MANAGER"] },
      { key: "pos-conflicts", label: "Till Sync Review (this device)", href: "/pos/conflicts", roles: ["OWNER", "BRANCH_MANAGER"] },
      { key: "assets", label: "Fixed Assets", href: "/assets", roles: [...ROLES_WITH_ASSETS_ACCESS] },
    ],
  },
  {
    key: "system",
    // Section header is "Settings", not "System" — the section's one nested item is
    // itself labeled "System" (the merged Notifications/Webhooks/Branding page), and a
    // section header identical to its own only item's label is both confusing in the UI
    // and literally ambiguous to `screen.queryByText` in tests.
    label: "Settings",
    items: [{ key: "system", label: "System", href: "/system", roles: [...ROLES_WITH_BRANDING_ACCESS] }],
  },
];

/** Flat lookup of every nav-linked route (basics + all section items) — used by
 * `NavHeader` to resolve the current page's `activeKey` regardless of whether the active
 * route lives in the always-visible basics or a collapsed Advanced section. */
export const ALL_NAV_ITEMS: NavItem[] = [...BASIC_NAV_ITEMS, ...NAV_SECTIONS.flatMap((section) => section.items)];

/**
 * `/pos/*` — ported from `pos-pwa/src/roles/permissions.ts` as part of that app's merge
 * into this one. Prefixed `POS_` deliberately, rather than reusing the bare
 * `ROLES_WITH_RETURNS_ACCESS`/`ROLES_WITH_CONFLICTS_ACCESS` names above: those already
 * mean something different here — the back-office fulfillment-returns flow and the
 * server-truth resolvable `/conflicts` queue (`GET/PATCH /sync/conflicts`), vs. the
 * counter-side quick-return flow and a *device-local*, read-only view of this till's own
 * not-yet-confirmed conflicted events (from this device's own RxDB, not the server). Same
 * label, genuinely different features — see `pages/PosConflictsPage.tsx` vs.
 * `pages/conflicts/ConflictsPage.tsx`.
 *
 * | Role            | `/pos/*` access                                                 |
 * |-----------------|-------------------------------------------------------------------|
 * | TELLER          | Till only — their assigned branch's catalog, no reports/ledger/settings |
 * | BRANCH_MANAGER  | Till (full access: voids/discounts) + branch-level device-local conflicts queue |
 * | OWNER           | Treated as BRANCH_MANAGER-equivalent for `/pos/*` purposes — not excluded from any POS capability; their *additional* surfaces (user/branch management, cross-branch reports) live in the back-office routes above, out of `/pos`'s scope |
 * | ACCOUNTANT      | Not authorized — POS operational actions aren't part of this role |
 * | ECOM_MGR        | Not authorized — POS till operations are explicitly excluded |
 *
 * As with every constant above, this is a UX convenience only — the backend's own
 * `require_roles(...)` dependencies (and, for offline-originated writes, whatever the sync
 * engine validates server-side on replay) are the actual boundary.
 */
export const POS_ROLES_WITH_TILL_ACCESS: readonly AppRole[] = ["TELLER", "BRANCH_MANAGER", "OWNER"];
export const POS_ROLES_WITH_MANAGER_ACTIONS: readonly AppRole[] = ["BRANCH_MANAGER", "OWNER"];
export const POS_ROLES_WITH_CONFLICTS_ACCESS: readonly AppRole[] = ["BRANCH_MANAGER", "OWNER"];
/** Same floor as `POST /returns`'s own backend role gate
 * (`require_roles(OWNER, BRANCH_MANAGER)`, `backend/app/api/v1/returns.py`): "a TELLER
 * accidentally being able to refund arbitrary amounts is a real risk." */
export const POS_ROLES_WITH_RETURNS_ACCESS: readonly AppRole[] = ["BRANCH_MANAGER", "OWNER"];

export function canAccessTill(role: AppRole): boolean {
  return POS_ROLES_WITH_TILL_ACCESS.includes(role);
}

export function canVoidOrDiscount(role: AppRole): boolean {
  return POS_ROLES_WITH_MANAGER_ACTIONS.includes(role);
}

export function canAccessPosConflicts(role: AppRole): boolean {
  return POS_ROLES_WITH_CONFLICTS_ACCESS.includes(role);
}

/** Feeds `@shop-ledger/ui-components`' `NavShell` inside `PosNavHeader` — the single
 * declared source of "what `/pos/*` routes exist," consumed by both the kiosk nav (hide)
 * and the route guards in `App.tsx` (deny). Labels/hrefs match `pos-pwa`'s own
 * `NAV_ITEMS` exactly (now under the `/pos` prefix) so nothing about the till's own UX
 * changes because of the merge. */
export const POS_NAV_ITEMS: NavItem[] = [
  { key: "pos-till", label: "Till", href: "/pos/till", roles: [...POS_ROLES_WITH_TILL_ACCESS] },
  // Quick Return and Needs Review (this device) are deliberately NOT listed here anymore
  // (business-owner request: "hide needs review and quick return from the pos, move those
  // features to dashboard admin") — their routes and RequireRole guards still exist under
  // /pos/*, but they're reached via the back-office nav's Operations section
  // (`NAV_SECTIONS` above), which a TELLER never sees. The counter keeps only what a
  // counter needs: sell, review own sales, set up the printer.
  // Every till-access role for My Sales, no manager-only gate — seeing your own past sales
  // isn't a risk that needs restricting, unlike Quick Return/void/discount.
  { key: "pos-my-sales", label: "My Sales", href: "/pos/my-sales", roles: [...POS_ROLES_WITH_TILL_ACCESS] },
  { key: "pos-printer-setup", label: "Printer Setup", href: "/pos/printer-setup", roles: [...POS_ROLES_WITH_TILL_ACCESS] },
];
